import { useCallback, useEffect, useRef, useState } from 'react';
import { BannerText, BuilderPageProvider, Benefits, Banner } from '@divlab/divan-constructor';

import * as Meta from '@Queries/Meta';
import * as Compare from '@Queries/Compare';
import * as HeaderStore from '@Stores/Header';
import styles from './PageHranenie.module.css';
import usePage from '@Queries/usePage';
import LayoutMain from '@Layouts/LayoutMain';
import Page from '@Components/Page';
import PageTopBar from '@Components/PageTopBar';
import Categories from './elems/Categories';
import CatalogBanner from './elems/CatalogBanner';
import defaultData from './defaultData';
import { useDeps } from '@Contexts/DI';
import { useRequest } from '@Contexts/Request';
import useTranslation from '@Queries/useTranslation';
import Link from '@Navigation/Link';

import type { FC } from 'react';
import type {
  PageBackendDTO,
  PageData,
  PageFrontendDTO,
  CategoryData,
  BenefitData,
} from './typings';

const pageName = 'Лендинг Хранение';

const PageHranenie: FC = () => {
  const [data, setData] = useState<PageData>(null);
  const { data: backendDTO } = usePage<PageBackendDTO>();
  const { t } = useTranslation();
  const pageRef = useRef<HTMLDivElement>(null);
  const { totalHeight } = HeaderStore.useSizes();

  function getCurrentDate() {
    const date = new Date();

    const format = (value: number) => `${value}`.padStart(2, '0') || '';

    const day = format(date.getDate());
    const mounth = format(date.getMonth() + 1);
    const year = format(date.getFullYear());

    return `${day}.${mounth}.${year}`;
  }

  const getData = useCallback(
    (frontendDTO: PageFrontendDTO) => {
      const categories = backendDTO.categories.map((category) => {
        const type = category.name;

        const newCategory: CategoryData = {
          title: t(`furniture.items.${type}.title`),
          description: t(`furniture.items.${type}.description`),
          hash: frontendDTO.categories[type]?.hash,
          tiles: {
            images: {
              desktop: frontendDTO.categories[type].tiles.desktop,
              mobile: frontendDTO.categories[type].tiles.mobile,
            },
            button: t('ui.more'),
          },
          image: frontendDTO.categories[type]?.image,
          products: category.products || [],
          pins: category.pins || [],
          pinsCoords: frontendDTO.categories[type]?.coords || [],
          button: t('ui.see-all'),
          link: frontendDTO.categories[type]?.link,
        };

        return newCategory;
      });

      const benefits = frontendDTO.benefits.map((benefit, index) => {
        const newBenefit: BenefitData = {
          text: t(`benefits.items.${index + 1}`),
          image: benefit.image,
        };

        return newBenefit;
      });

      const newData: PageData = {
        breadcrumbs: backendDTO.breadcrumbs,
        banner: {
          title: t('banner.title'),
          description: t('banner.description'),
          condition: `${t('banner.condition')} ${getCurrentDate()} г.`,
          assets: frontendDTO.banner,
        },
        description: t('description'),
        rubrics: frontendDTO.rubrics,
        categories: {
          title: t('furniture.title'),
          description: t('furniture.description'),
          items: categories || [],
        },
        catalogBanner: frontendDTO.catalog,
        benefits: {
          title: t('benefits.title'),
          items: benefits || [],
        },
      };

      return newData;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [backendDTO.breadcrumbs],
  );

  useEffect(() => {
    if (backendDTO) {
      const newData = getData(defaultData);
      setData(newData);
    }
  }, [backendDTO, getData]);

  const scrollToElem = useCallback(
    (elem: HTMLDivElement) => {
      const rect = elem.getBoundingClientRect();
      const top = window.scrollY + rect.top - totalHeight;

      window.scrollTo({ top, behavior: 'smooth' });
    },
    [totalHeight],
  );

  useEffect(() => {
    if (!data) return;
    if (!pageRef?.current) return;

    const hash = window.location.hash;

    if (!hash) return;

    const elem = pageRef.current.querySelector<HTMLDivElement>(`${hash}`);

    if (!elem) return;

    scrollToElem(elem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    data && (
      <LayoutMain>
        <Page>
          <PageTopBar breadcrumbs={data.breadcrumbs} />
          <div className={styles.page}>
            <Banner
              soundMode='disabled'
              contents={{
                title: data.banner.title,
                subtitle: data.banner.description,
                notification: data.banner.condition,
              }}
              assets={data.banner.assets}
              className={styles.banner}
              view='primary'
            />
            <BannerText text={data.description} className={styles.description} />
            <Categories
              className={styles.categories}
              classNameCategory={styles.inContainer}
              categories={data.categories.items}
              analylicsTitle={pageName}
            />
            <div className={styles.catalogBanner}>
              <CatalogBanner
                cnTitle={styles.blockTitle}
                className={styles.catalog}
                bannerData={data.catalogBanner}
              />
            </div>
            <Benefits className={styles.benefits} data={data.benefits} />
          </div>
        </Page>
      </LayoutMain>
    )
  );
};

const PageHranenieDataWrapper = () => {
  const currentDate = new Date();
  const request = useRequest();
  const DI = useDeps();
  const { device } = useRequest();
  const { data: meta } = Meta.useMeta();

  const builderData = {
    request,
    DI,
    components: {
      Link,
    },
    device,
    currentDate,
    meta,
    Queries: {
      Compare,
    },
  };

  return (
    <BuilderPageProvider value={{ ...builderData }}>
      <PageHranenie />
    </BuilderPageProvider>
  );
};

export default PageHranenieDataWrapper;
